// ---------------------------------------------------------------------------
// Selection types
//
// Composed logo: filter the pooled exploration samples, compose the palette,
//                verify its spatial repeatability.
// Mode contrast: the same steps (the repeatability always in two capture
//                modes), then the Mode Contrast Filter: groups of samples
//                close in one capture mode and far apart in the other.
// Manual logo:   the user picks every palette sample by hand, off the marked
//                patch groups or by sample ID; the palette is final once
//                picked (no filters, composition or repeatability test).
//
// The stored values are the backend's SelectionType. LOGO_SELECTION predates
// the other two and is the composed logo selection.
// ---------------------------------------------------------------------------
export const LOGO_SELECTION = "LOGO_SELECTION";
export const MODE_CONTRAST_SELECTION = "MODE_CONTRAST_SELECTION";
export const MANUAL_LOGO_SELECTION = "MANUAL_LOGO_SELECTION";
// Image:         primaries for halftoning (image and gradient reproductions):
//                filter, rank, spatial repeatability, then the width group,
//                the automatic primaries and the predictability test.
export const IMAGE_SELECTION = "IMAGE_SELECTION";

export function isImageSelection(selectionType) {
    return (selectionType || "").toUpperCase() === IMAGE_SELECTION;
}

export function isModeContrastSelection(selectionType) {
    return (selectionType || "").toUpperCase() === MODE_CONTRAST_SELECTION;
}

export function isManualSelection(selectionType) {
    return (selectionType || "").toUpperCase() === MANUAL_LOGO_SELECTION;
}

// ---------------------------------------------------------------------------
// Selection routes
//
// The kind is a URL segment and every page about one selection carries its
// id, so two tabs can hold two selections:
//
//     /colorselections/<kind>                    the list of that kind
//     /colorselections/<kind>/new/<step>         the create wizard (name, explorations)
//     /colorselections/<kind>/<selectionId>/<step>
// ---------------------------------------------------------------------------
export const SELECTION_TYPE_CHOICE_ROUTE = "/colorselectiontype";
export const SELECTIONS_BASE = "/colorselections";

/** Wording and routes per selection type, keyed by the SELECTION_KINDS key;
 *  ``slug`` is the kind's URL segment. */
export const SELECTION_KINDS = {
    logo: {
        type: LOGO_SELECTION,
        slug: "logo",
        typeLabel: "Composed Logo Selection",
        navLabel: "Composed logo",
        listRoute: `${SELECTIONS_BASE}/logo`,
        listTitle: "Composed logo selections",
        newLabel: "New composed selection",
        emptyHint: "No composed logo selections yet — create the first one.",
        emptyHintMine: "You have no composed logo selections yet — create one.",
        sidebarLabel: "COMPOSED LOGO SELECTION",
        // The step after Choose Explorations.
        afterExplorationsStep: "pruning",
    },
    modeContrast: {
        type: MODE_CONTRAST_SELECTION,
        slug: "modecontrast",
        typeLabel: "Mode Contrast Selection",
        navLabel: "Mode contrast",
        listRoute: `${SELECTIONS_BASE}/modecontrast`,
        listTitle: "Mode contrast selections",
        newLabel: "New mode contrast selection",
        emptyHint: "No mode contrast selections yet — create the first one.",
        emptyHintMine: "You have no mode contrast selections yet — create one.",
        sidebarLabel: "MODE CONTRAST SELECTION",
        afterExplorationsStep: "pruning",
    },
    manual: {
        type: MANUAL_LOGO_SELECTION,
        slug: "manual",
        typeLabel: "Manual Logo Selection",
        navLabel: "Manual logo",
        listRoute: `${SELECTIONS_BASE}/manual`,
        listTitle: "Manual logo selections",
        newLabel: "New manual selection",
        emptyHint: "No manual logo selections yet — create the first one.",
        emptyHintMine: "You have no manual logo selections yet — create one.",
        sidebarLabel: "MANUAL LOGO SELECTION",
        afterExplorationsStep: "plates",
    },
    image: {
        type: IMAGE_SELECTION,
        slug: "image",
        typeLabel: "Image Selection",
        navLabel: "Image",
        listRoute: `${SELECTIONS_BASE}/image`,
        listTitle: "Image selections",
        newLabel: "New image selection",
        emptyHint: "No image selections yet — create the first one.",
        emptyHintMine: "You have no image selections yet — create one.",
        sidebarLabel: "IMAGE SELECTION",
        afterExplorationsStep: "pruning",
    },
};

export const SELECTION_KIND_KEYS = Object.keys(SELECTION_KINDS);

/** The SELECTION_KINDS key of a URL segment (unknown -> logo). */
export function selectionKindKeyForSlug(slug) {
    return SELECTION_KIND_KEYS.find((key) => SELECTION_KINDS[key].slug === slug) || "logo";
}

/** The steps a selection page can be at (URL segments). */
export const SELECTION_STEPS = [
    "explorations", "pruning", "composition", "prepass", "spatial", "repeatability",
    "modecontrast", "overview", "plates", "search", "widthgroups", "primaries",
    "predictability",
];

/** The create wizard of a kind: ``name`` then ``explorations``. */
export const SELECTION_SETUP_STEPS = ["name", "explorations"];
export function selectionSetupRoute(selectionType, step) {
    return `${selectionKind(selectionType).listRoute}/new/${step}`;
}

/** A page of one selection. */
export function selectionRoute(selectionType, selectionId, step) {
    return `${selectionKind(selectionType).listRoute}/${selectionId}/${step}`;
}

/** The SELECTION_KINDS key of a selection type: logo for anything that is
 *  neither mode contrast nor manual, as every older logo selection is. */
export function selectionKindKey(selectionType) {
    if (isModeContrastSelection(selectionType)) return "modeContrast";
    if (isManualSelection(selectionType)) return "manual";
    if (isImageSelection(selectionType)) return "image";
    return "logo";
}

/** The SELECTION_KINDS entry of a selection type. */
export function selectionKind(selectionType) {
    return SELECTION_KINDS[selectionKindKey(selectionType)];
}

// Shared definition of the composed logo-selection wizard for the step sidebar.
const SELECTION_FLOW_STEPS = [
    { key: "setup", label: "Setup" },
    { key: "explorations", label: "Choose Explorations" },
    { key: "pruning", label: "Design & Performance Space Filtering" },
    { key: "composition", label: "Composition" },
    { key: "prepass", label: "Prepass" },
    { key: "spatial", label: "Spatial Repeatability Measurement" },
    { key: "review", label: "Spatial Repeatability Filter" },
    { key: "overview", label: "Overview" },
];

// The mode contrast selection: the logo steps with the Mode Contrast Filter
// before the overview.
const MODE_CONTRAST_SELECTION_FLOW_STEPS = [
    ...SELECTION_FLOW_STEPS.slice(0, -1),
    { key: "modecontrast", label: "Mode Contrast Filter" },
    SELECTION_FLOW_STEPS[SELECTION_FLOW_STEPS.length - 1],
];

// The manual logo selection: no filters, no composition and no
// repeatability test; the palette is picked on the plates and by sample ID,
// then its prepass is chosen and the selection finished.
const MANUAL_SELECTION_FLOW_STEPS = [
    { key: "setup", label: "Setup" },
    { key: "explorations", label: "Choose Explorations" },
    { key: "plates", label: "Pick on Plates" },
    { key: "search", label: "Find by Sample ID" },
    { key: "prepass", label: "Prepass" },
    { key: "overview", label: "Overview" },
];

// The image selection (the colour-selection script's stages): rank instead
// of compose, then the prepass, the repeatability, the width group, its
// primaries and their predictability.
const IMAGE_SELECTION_FLOW_STEPS = [
    ...SELECTION_FLOW_STEPS.slice(0, 3),
    { key: "composition", label: "Ranking" },
    ...SELECTION_FLOW_STEPS.slice(4, -1),
    { key: "widthgroups", label: "Width Groups" },
    { key: "primaries", label: "Primaries" },
    { key: "predictability", label: "Predictability Test" },
    SELECTION_FLOW_STEPS[SELECTION_FLOW_STEPS.length - 1],
];

const FLOW_STEPS_BY_KIND = {
    logo: SELECTION_FLOW_STEPS,
    modeContrast: MODE_CONTRAST_SELECTION_FLOW_STEPS,
    manual: MANUAL_SELECTION_FLOW_STEPS,
    image: IMAGE_SELECTION_FLOW_STEPS,
};

// The image selection repeats these steps per iteration: taking primaries
// out after a predictability round starts the next one at the width groups.
export const IMAGE_ITERATION_STEPS = ["widthgroups", "primaries", "predictability"];

/** One line per earlier iteration: "Iteration 1 · 60 µm · 6 primaries · ΔE00 14.6 · 1 taken out". */
export function iterationSummary(entry) {
    const parts = [`Iteration ${entry.iteration}`];
    if (entry.width_group) parts.push(`${entry.width_group} µm`);
    if (entry.primary_count) parts.push(`${entry.primary_count} primaries`);
    if (entry.verdict?.mean != null) parts.push(`ΔE00 ${Number(entry.verdict.mean).toFixed(1)}`);
    const takenOut = entry.taken_out?.length ?? 0;
    if (takenOut) parts.push(`${takenOut} taken out`);
    return parts.join(" · ");
}

/**
 * Step sidebar of the selection wizard. ``step`` is the 0-based index or the
 * key of the current step; pages shared by several types (Setup, Choose
 * Explorations, Overview, ...) pass the key and the selection type, so the
 * right flow and position are shown. An image selection's pages pass its
 * ``iterations`` ({current, history} from the backend): the iteration steps
 * are then shown as a loop with the current iteration and the earlier ones.
 */
export function selectionSidebar(step, sampleCount = null, selectionType = LOGO_SELECTION, iterations = null) {
    const steps = FLOW_STEPS_BY_KIND[selectionKindKey(selectionType)];
    const currentIndex = typeof step === "string"
        ? Math.max(steps.findIndex((entry) => entry.key === step), 0)
        : step;
    const sidebar = {
        steps,
        currentIndex,
        iterationValue: sampleCount,
        iterationLabel: selectionKind(selectionType).sidebarLabel,
        valueCaption: "Samples",
    };
    if (iterations && isImageSelection(selectionType)) {
        const current = iterations.current ?? 1;
        sidebar.loop = {
            stepKeys: IMAGE_ITERATION_STEPS,
            label: `Iteration ${current}`,
            earlier: (iterations.history ?? []).filter((entry) => entry.iteration !== current).map(iterationSummary),
        };
    }
    return sidebar;
}

/** Status presentation for selection cards: friendly label + accent colour. */
export function selectionStatusPresentation(status) {
    const normalized = (status || "").toUpperCase();
    switch (normalized) {
        case "PENDING":
            return { label: "Not started", color: "#f59e0b" };
        case "WIDTH_FILTER":
        case "TIME_FILTER":
        case "CHROMATIC_FILTER":
            return { label: "Filtering", color: "#4C7EFF" };
        case "COMPOSITION":
            return { label: "Palette composed", color: "#4C7EFF" };
        case "GENERATE_SPATIAL_FILES":
            return { label: "Spatial marking", color: "#4C7EFF" };
        case "SPATIAL_MEASURED":
            return { label: "Repeatability measured, filter open", color: "#4C7EFF" };
        case "SPATIAL_REPETABLE_TEST":
            return { label: "Repeatability measured", color: "#16a34a" };
        case "MODE_CONTRAST":
            return { label: "Mode contrast filter open", color: "#4C7EFF" };
        case "WIDTH_GROUPS":
            return { label: "Primaries open", color: "#4C7EFF" };
        case "REFERENCE":
            return { label: "Primaries fixed", color: "#4C7EFF" };
        case "PREDICTABILITY":
            return { label: "Predictability test", color: "#4C7EFF" };
        case "WIDTH_GROUP_CHOICE":
            return { label: "Width group choice", color: "#4C7EFF" };
        case "GAMUT_OPTIMIZATION":
            return { label: "Gamut optimised", color: "#16a34a" };
        case "COMPLETED":
            return { label: "Completed", color: "#16a34a" };
        default:
            return {
                label: normalized
                    ? normalized.toLowerCase().split("_").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ")
                    : "Unknown",
                color: "#9ca3af",
            };
    }
}

/** A finished selection (repeatability measured) is read-only: the wizard
 *  steps may no longer be entered, only the overview. */
const FINALIZED_SELECTION_STATUSES = [
    "SPATIAL_REPETABLE_TEST", "GAMUT_OPTIMIZATION", "COMPLETED",
];

export function isSelectionFinalized(status) {
    return FINALIZED_SELECTION_STATUSES.includes((status || "").toUpperCase());
}

/** Where a step before the Mode Contrast Filter sends a selection it may no
 *  longer change: the overview once finished, the Mode Contrast Filter once
 *  the repeatability is confirmed; null while the step is still editable. */
export function selectionLockedRoute(selectionId, status, selectionType = LOGO_SELECTION) {
    const step = selectionLockedStep(status);
    return step ? selectionRoute(selectionType, selectionId, step) : null;
}

function selectionLockedStep(status) {
    const normalized = (status || "").toUpperCase();
    if (isSelectionFinalized(status)) return "overview";
    if (normalized === "MODE_CONTRAST") return "modecontrast";
    if (normalized === "WIDTH_GROUPS") return "widthgroups";
    if (normalized === "PREDICTABILITY") return "predictability";
    if (IMAGE_PRIMARIES_FIXED_STATUSES.includes(normalized)) return "overview";
    return null;
}

/** Image selection statuses after the primaries are fixed. */
const IMAGE_PRIMARIES_FIXED_STATUSES = ["REFERENCE", "PREDICTABILITY", "WIDTH_GROUP_CHOICE"];

export function areImagePrimariesFixed(status) {
    return IMAGE_PRIMARIES_FIXED_STATUSES.includes((status || "").toUpperCase());
}

/** Route for resuming a selection at the step matching its status. A manual
 *  selection is either still being picked or finished. */
export function selectionResumeRoute(selectionId, status, selectionType = LOGO_SELECTION) {
    return selectionRoute(selectionType, selectionId, selectionResumeStep(status, selectionType));
}

function selectionResumeStep(status, selectionType) {
    if (isManualSelection(selectionType)) {
        return isSelectionFinalized(status) ? "overview" : "plates";
    }
    switch ((status || "").toUpperCase()) {
        case "PENDING":
        case "WIDTH_FILTER":
        case "TIME_FILTER":
        case "CHROMATIC_FILTER":
            return "pruning";
        // Composed: the prepass is chosen next; once the repeatability
        // plates exist the prepass is baked into them.
        case "COMPOSITION":
            return "prepass";
        case "GENERATE_SPATIAL_FILES":
            return "spatial";
        case "SPATIAL_MEASURED":
            return "repeatability";
        case "MODE_CONTRAST":
            return "modecontrast";
        case "WIDTH_GROUPS":
            return "widthgroups";
        case "PREDICTABILITY":
            return "predictability";
        default:
            return "overview";
    }
}

// ---------------------------------------------------------------------------
// Selection identifiers
//
// A selection may source samples from several explorations, so its identifier
// is NOT derived from an exploration name. It is composed from the basis the
// user picks on the "Setup" page plus the day it was named:
//
//     <Substrate>-<Laser>-<YYYYMMDD>-<Tag>      e.g. SS-AISI304-IPG2020-20260620-SEL001
//
// Substrate = the leading `<Material>-<Alloy>` part of the material name,
// Laser     = the laser_device_name as stored,
// Tag       = free text typed by the user (pre-filled with the next free
//             SEL### number for that substrate/laser pair as a suggestion).
// Laser and material are also stored on the Selection row as foreign keys;
// the name is only the human-readable handle.
// ---------------------------------------------------------------------------

// Any non-empty tag without leading/trailing whitespace. The name is never
// used as a storage path (buckets key on the selection UUID), so no further
// character restriction is needed.
export const SELECTION_TAG_PATTERN = /^\S(?:.*\S)?$/;

/** `SS-AISI304-U-F-1mm-RUPP` -> `SS-AISI304` (first two dash-separated parts). */
export function substrateToken(materialName) {
    return (materialName || "").split("-").filter(Boolean).slice(0, 2).join("-");
}

/** Local calendar date as YYYYMMDD. */
export function dateStamp(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
}

/** `<Substrate>-<Laser>` — the part of the identifier that depends only on the basis. */
function selectionNamePrefix(materialName, laserName) {
    const substrate = substrateToken(materialName);
    return [substrate, laserName || ""].filter(Boolean).join("-");
}

export function composeSelectionName(materialName, laserName, tag, date = new Date()) {
    return `${selectionNamePrefix(materialName, laserName)}-${dateStamp(date)}-${tag}`;
}

/** Next free SEL### for a substrate/laser pair, counting existing names of
 *  that pair regardless of their date (and regardless of whether they still
 *  follow the older `...-V1.0-<date>-SEL###` layout). */
export function nextSelectionTag(existingNames, materialName, laserName) {
    const prefix = `${selectionNamePrefix(materialName, laserName)}-`;
    let highest = 0;
    (existingNames || []).forEach((name) => {
        if (typeof name !== "string" || !name.startsWith(prefix)) return;
        const match = /-SEL(\d{3})$/.exec(name);
        if (match) highest = Math.max(highest, parseInt(match[1], 10));
    });
    return `SEL${String(highest + 1).padStart(3, "0")}`;
}
